<template>
  <div
    class="flex text-strongest min-w-40"
    :class="[vertical ? 'flex-col gap-2 items-start' : 'flex-row items-center text-sm']"
  >
    <template v-for="(tab, index) in props.tabs">
      <!-- If this tab has subtabs -->
      <div v-if="tab.subtabs" class="relative" :class="vertical ? 'w-full' : 'w-auto'">
        <!-- Tab Button -->
        <div
          class="h-10 flex items-center px-3 py-2 cursor-pointer z-10 rounded-2xl w-full"
          :class="[
            tab.key === props.activeTab ? activeClass : '',
            vertical ? 'absolute' : 'relative',
            tab.subtabs.some((sub) => sub.key === props.activeTab)
              ? 'bg-tab-bg-selected'
              : 'bg-tab-bg-unselected',
          ]"
          @click="openTabMenu(tab.key)"
        >
          <Icon
            v-if="
              tab.icon ||
              tab.key === openTab ||
              (tab.subtabs.some((sub) => sub.key === props.activeTab) && props.useActiveIcon)
            "
            :name="tab.icon || props.activeIcon"
            class="w-4 h-4 mr-2.5 min-w-4"
            :class="
              tab.key === openTab || tab.subtabs.some((sub) => sub.key === props.activeTab)
                ? 'text-tab-icon-selected'
                : 'text-tab-icon-unselected'
            "
          />
          <span class="whitespace-nowrap font-inter"> {{ tab.label }}</span>
          <div
            v-if="tab.notif_badge_number"
            class="px-2.5 bg-red-600 text-white rounded-full text-sm flex items-center font-inter justify-center"
          >
            {{ tab.notif_badge_number > 99 ? "99+" : tab.notif_badge_number }}
          </div>
          <div class="grow"></div>
          <Icon
            name="ChevronDown"
            class="w-4 h-4 ml-2 transition min-w-4"
            :class="openTab === tab.key ? 'rotate-180' : ''"
          />
        </div>
        <!-- Hidden div to maintain parent div's width -->
        <div class="h-0 overflow-hidden">
          <div v-for="sub in tab.subtabs">
            <span class="whitespace-nowrap pl-18"> {{ sub.label }}</span>
          </div>
          <!-- <span class="whitespace-nowrap text-base pl-18.5"> {{ tab.label }}</span> -->
        </div>

        <!-- Subtab Menu -->
        <div
          class="left-0 transition-max-height duration-300 h-full overflow-hidden bg-paper-3 dark:bg-primary-4-t rounded-2xl px-3 pb-3 text-sm w-full"
          :class="[
            openTab === tab.key
              ? tab.subtabs.length > 4
                ? ''
                : 'max-h-64'
              : vertical
                ? 'max-h-10 bg-opacity-0 dark:bg-opacity-0 !pb-0'
                : 'max-h-0 bg-opacity-0 dark:bg-opacity-0 !pb-0',
            vertical ? 'relative pt-10' : 'absolute top-0 pt-10',
          ]"
        >
          <div
            v-for="sub in tab.subtabs"
            :key="`${tab.key}.${sub.key}`"
            class="mt-2 px-3 py-2 cursor-pointer hover:bg-tab-bg-hover rounded flex flex-row items-center"
            :class="sub.key === props.activeTab ? subActiveClass : ''"
            @click.stop="
              sub.action
                ? $emit('tab-action', {
                    action: sub.action,
                    key: sub.key,
                    tabKey: tab.key,
                    subKey: sub.key,
                  })
                : ($emit('update:activeTab', sub.key), vertical ? '' : (openTab = null))
            "
          >
            <Icon
              v-if="sub.icon"
              :name="sub.icon"
              class="w-3.5 h-3.5 mr-2.5 min-w-3.5"
              :class="
                tab.key === openTab ||
                (tab.subtabs.some((sub) => sub.key === props.activeTab) && props.useActiveIcon)
                  ? 'text-tab-icon-sub-selected'
                  : 'text-tab-icon-sub-unselected'
              "
            />
            <span class="whitespace-nowrap"> {{ sub.label }}</span>
            <div
              v-if="tab.notif_badge_number"
              class="px-2.5 bg-red-600 text-white rounded-full text-sm flex items-center font-inter justify-center"
            >
              {{ tab.notif_badge_number > 99 ? "99+" : tab.notif_badge_number }}
            </div>
          </div>
        </div>
      </div>

      <!-- If this tab has no subtabs -->
      <div
        v-else
        class="h-10 flex items-center px-3 py-2 cursor-pointer gap-2"
        :class="[tab.key === props.activeTab ? activeClass : '', vertical ? 'w-full' : 'w-auto']"
        @click="($emit('update:activeTab', tab.key), (openTab = null))"
      >
        <Icon
          v-if="tab.icon || (props.activeTab === tab.key && props.useActiveIcon)"
          :name="tab?.icon || props.activeIcon"
          class="w-4 h-4"
          :class="
            tab.key === props.activeTab ? 'text-tab-icon-selected' : 'text-tab-icon-unselected'
          "
        />
        <span class="whitespace-nowrap font-inter"> {{ tab.label }}</span>
        <div
          v-if="tab.notif_badge_number"
          class="px-2.5 bg-red-600 text-white rounded-full text-sm flex items-center font-inter justify-center"
        >
          {{ tab.notif_badge_number > 99 ? "99+" : tab.notif_badge_number }}
        </div>
      </div>
      <!-- Divider between items on horizontal -->
      <div
        v-if="index < props.tabs.length - 1 && !props.vertical"
        class="w-3.5 h-0.5 mx-3 bg-outline-dividers"
      ></div>
    </template>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { Icon } from "@/components";

// props
const props = defineProps({
  tabs: {
    type: Array,
    default: [],
  },
  activeTab: {
    type: String,
    default: "",
  },
  useActiveIcon: {
    type: Boolean,
    default: false,
  },
  activeIcon: {
    type: String,
    default: "Sticker",
  },
  vertical: {
    type: Boolean,
    default: false,
  },
});

const activeClass = ref("font-semibold bg-tab-bg-selected rounded-full");
const subActiveClass = ref("font-semibold bg-container-bg-dis-canvas rounded-full");
const openTab = ref(null);
// add near top
const emit = defineEmits(["update:activeTab", "tab-action"]);

defineExpose({
  openTab,
});

function openTabMenu(key) {
  // TODO better logic here
  openTab.value = key === openTab.value ? null : key;
}
</script>
